// Admin Protected Route - Verified Firebase Authentication
import React, { useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../utils/useAuth';
import { checkIsAdmin, isAdminEmailFallback } from '../utils/adminService';

interface AdminProtectedRouteProps {
    children: React.ReactNode;
}

export const AdminProtectedRoute: React.FC<AdminProtectedRouteProps> = ({ children }) => {
    const { user, loading: authLoading, isAuthenticated } = useAuth();
    const [isAdmin, setIsAdmin] = useState<boolean | null>(null);
    const [checkingAdmin, setCheckingAdmin] = useState(true);

    useEffect(() => {
        const verifyAdmin = async () => {
            if (!authLoading && isAuthenticated && user?.email) {
                try {
                    // Check Firestore first
                    const firestoreAdmin = await checkIsAdmin(user.email);
                    // Check Fallback list second
                    const fallbackAdmin = isAdminEmailFallback(user.email);
                    const result = firestoreAdmin || fallbackAdmin;

                    console.log('🛡️ Admin Check for:', user.email, 'Result:', result);
                    setIsAdmin(result);
                } catch (error) {
                    console.error('Admin verification failed:', error);
                    setIsAdmin(false);
                } finally {
                    setCheckingAdmin(false);
                }
            } else if (!authLoading) {
                console.log('👤 User not authenticated, blocking admin access');
                setIsAdmin(false);
                setCheckingAdmin(false);
            }
        };

        verifyAdmin();
    }, [authLoading, isAuthenticated, user]);

    // While loading auth or checking admin status
    if (authLoading || checkingAdmin) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gray-50">
                <div className="text-center">
                    <div className="w-12 h-12 border-4 border-brand-lime border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                    <p className="text-gray-500 font-medium">Verifying Credentials...</p>
                </div>
            </div>
        );
    }

    // Redirect to home if not an admin
    if (!isAdmin) {
        console.warn('⛔ Unauthorized access attempt to Admin Dashboard');
        return <Navigate to="/" replace />;
    }

    return <>{children}</>;
};

export default AdminProtectedRoute;
